import memoizeOne from "memoize-one";
import PropTypes from "prop-types";
import React from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import FormButton from "../../components/form/FormButton";
import Icon from "../../components/interface/Icon";
import { uiStateOperations } from "../../modules/uiState";
import getActionForResolvedTips from "../../tutorial/getActionForResolvedTips";
import { onTutorialLinkClick } from "../../tutorial/tutorialLinks";
import { replaceTutorialPlaceholders } from "../../tutorial/utils";
import TutorialTipStepsView from "./TutorialTipStepsView";

const getOnNextClickFunction = memoizeOne((activeTutorial, completedTipSet, dispatchAction, onNextClick) => tipId => {
  const resolved = [tipId];
  const fallbackBrowserAction = getActionForResolvedTips(resolved, activeTutorial, completedTipSet);
  if (fallbackBrowserAction) {
    dispatchAction(fallbackBrowserAction);
  }
  if (onNextClick) {
    onNextClick();
  }
});

const getReplaceTextFunction = memoizeOne(
  (sidebarTypes, orderedNarrativeTypes, typesById, defaultSequenceType, narrativeSectionTypes, syncAppName) => text =>
    replaceTutorialPlaceholders(
      text,
      sidebarTypes,
      orderedNarrativeTypes,
      typesById,
      defaultSequenceType,
      narrativeSectionTypes,
      syncAppName
    )
);

const onLinkClickFunction = linkId => onTutorialLinkClick(linkId);

const getHandleFlashFunction = memoizeOne(dispatchAction => interfaceId =>
  dispatchAction(uiStateOperations.pulsateIcon(interfaceId))
);

const TutorialTipView = props => {
  const { tip, tipCompleted, blockedTips, isEditMode, isNotification } = props;

  const replaceTextFunction = getReplaceTextFunction(
    props.sidebarTypes,
    props.orderedNarrativeTypes,
    props.typesById,
    props.defaultSequenceType,
    props.narrativeSectionTypes,
    props.syncAppName
  );
  const onNextClickFunction = getOnNextClickFunction(
    props.activeTutorial,
    props.completedTipSet,
    props.dispatchAction,
    props.onNextClick
  );

  const onFlashClickFunction = getHandleFlashFunction(props.dispatchAction);

  const steps = (
    <TutorialTipStepsView
      tip={tip}
      className={props.className}
      isEditMode={isEditMode}
      replaceTextCallback={replaceTextFunction}
      onLinkClick={onLinkClickFunction}
      onFlashClick={onFlashClickFunction}
    />
  );

  const isBlocked = blockedTips.has(tip.id);
  const isDismissable = tip.requiresDismissal && props.tipIsDismissable;

  const tickIcon = tipCompleted ? (
    <Icon icon="statusValid" color="statusValid" />
  ) : (
    <Icon icon="statusOpen" color="iconMono" />
  );

  const isActiveTip = props.isActive && !tip.isComplete;
  const expandIcon = isActiveTip ? "blank" : props.showDetail ? "upArrow" : "downArrow";
  const onExpandClick = isActiveTip ? null : () => props.onExpandClick(tip.id);

  const showNextButton = props.isActive && !tip.isComplete && (isDismissable || tip.canSkip);
  const nextButtonLabel = isDismissable ? "Mark Complete" : "Skip";

  const showPerformAction = props.isActive && !showNextButton && (isNotification || !tipCompleted);
  const performActionLabel = tipCompleted
    ? "Complete... loading next step"
    : tip.actionInstruction
      ? replaceTextFunction(tip.actionInstruction)
      : "Perform above steps to continue";

  return (
    <React.Fragment>
      <div className="tipTitle" onClick={onExpandClick}>
        {tickIcon}
        <span className={props.className}>{replaceTextFunction(tip.label)}</span>
        {props.showExpandIcon && expandIcon ? (
          <Icon icon={expandIcon} color="iconMono" onClick={onExpandClick} />
        ) : null}
      </div>
      {props.showDetail ? (
        isBlocked ? (
          <div className="tipStep">{replaceTextFunction(tip.gateMessage)}</div>
        ) : (
          steps
        )
      ) : null}
      {props.showDetail && showNextButton ? (
        <div className="tipNext">
          <FormButton onClick={() => onNextClickFunction(tip.id)} label={nextButtonLabel} linkStyle={true} />
        </div>
      ) : null}
      {props.showDetail && showPerformAction ? (
        <div className="tipNext performAction">{performActionLabel}&nbsp;</div>
      ) : null}
    </React.Fragment>
  );
};

TutorialTipView.propTypes = {
  // Coming directly from parent
  tip: PropTypes.object,
  className: PropTypes.string,
  isNotification: PropTypes.bool, // is this showing inside notification or panel?
  tipCompleted: PropTypes.bool,
  isActive: PropTypes.bool,
  tipIsDismissable: PropTypes.bool,
  activeTutorial: PropTypes.string,

  // inline can expand/collapse, also shows no detail in notification with another notification open
  showDetail: PropTypes.bool,

  // Expansion: used in Tutorial panel inline only
  showExpandIcon: PropTypes.bool,
  onExpandClick: PropTypes.func,

  // Next click: notification needs to know when user clicks next so it does not delay loading
  // the next tip
  onNextClick: PropTypes.func,

  // Variables to calculate string replacements
  sidebarTypes: ImmutablePropTypes.list.isRequired,
  orderedNarrativeTypes: ImmutablePropTypes.list.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  defaultSequenceType: PropTypes.string.isRequired,
  narrativeSectionTypes: ImmutablePropTypes.list.isRequired,
  syncAppName: PropTypes.string,
  isEditMode: PropTypes.bool,

  // Other stuff
  blockedTips: PropTypes.object,
  completedTipSet: ImmutablePropTypes.set.isRequired,
  dispatchAction: PropTypes.func.isRequired
};

export default TutorialTipView;
